import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { FiPlus } from 'react-icons/fi'

import { useQuery } from '@tanstack/react-query'
import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import ListPagination from '~/components/ListPagination'
import Table from '~/components/Table'
import { getRetailMediaAccountType } from '~/helpers'
import { useSidePanel } from '~/hooks/useSidePanel'
import { listKeywordsService } from '~/modules/retailMedia/services/campaign-additional-keywords'

import FormNewCampaignKeyWord from './FormNewKeyword'
import { getSchemaKeywords } from './schemas/schemaKeywords'

type Props = {
  campaignId: string
  panelIsOpen?: boolean
  isNegative?: boolean
  totalKeywords?: number
  sidePanelOpen?: boolean
  setSidePanelOpen?: (value: boolean) => void
  onTotalChange?: (value: number) => void
} & React.HTMLAttributes<HTMLElement>

const EditAdditionalKeywordsTable = ({
  campaignId,
  panelIsOpen = false,
  isNegative = false,
  totalKeywords = 0,
  sidePanelOpen,
  setSidePanelOpen,
  onTotalChange,
  ...props
}: Props) => {
  const [page, setPage] = useState(1)
  const [quantity, setQuantity] = useState(50)
  const accountType = getRetailMediaAccountType()
  const canEdit = accountType === 'advertiser'
  const { addSidePanel, removeSidePanel } = useSidePanel()

  const QUERY_KEY = [
    'get-additional-keywords',
    campaignId,
    page,
    quantity,
    isNegative
  ]

  const { status, data: { data, total } = {} } = useQuery({
    enabled: panelIsOpen && !sidePanelOpen,
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data } = await listKeywordsService({
        campaignId,
        page,
        quantity,
        count: true,
        is_negative: isNegative
      })
      return data
    }
  })

  useEffect(() => {
    onTotalChange?.(total || 0)
  }, [total])

  const handleOpenPanel = useCallback(() => {
    setSidePanelOpen(true)
    addSidePanel({
      title: isNegative
        ? t('rm:campaign.keywords.negative_keyword.new.title')
        : t('rm:campaign.keywords.additional_keyword.new.title'),
      type: 'content',
      content: (
        <FormNewCampaignKeyWord
          campaignId={campaignId}
          isNegative={isNegative}
          currentTotal={totalKeywords}
          callback={() => {
            setSidePanelOpen(false)
            removeSidePanel()
          }}
        />
      )
    })
  }, [
    addSidePanel,
    campaignId,
    removeSidePanel,
    isNegative,
    totalKeywords,
    setSidePanelOpen
  ])

  const schemaKeywords = useMemo(() => {
    return getSchemaKeywords({
      negative: isNegative,
      addButton: canEdit ? (
        <Button
          onClick={handleOpenPanel}
          text={t('rm:campaign.table.add.keyword')}
          iconLeft={<FiPlus />}
          size="small"
          customWidth="auto"
          template="secondary"
        />
      ) : null
    })
  }, [isNegative, getSchemaKeywords, handleOpenPanel])

  return (
    <section {...props}>
      <>
        <Table
          schema={schemaKeywords}
          data={data || []}
          total={total}
          striped
          sideBorder
          smallerRowPadding
          emptyStateText={t('rm:campaign.keywords.empty')}
          isLoading={status === 'pending'}
        />
        <div className="mx-4">
          <ListPagination
            currentPage={page}
            itemsPerPage={quantity}
            menuPlacement="top"
            onClickPagination={setPage}
            onItemsPerPageChange={({ value }) => setQuantity(value)}
            total={total}
            label={t('rm:keywords')}
          />
        </div>
      </>
    </section>
  )
}

export default EditAdditionalKeywordsTable
